import { formatSharesNumeral, sharesToEnglishWords } from '../../share-certificate/composables/sharesWords';
import { syncConsiderationWordFields } from './considerationWords';

export interface FlattenedTransferRow {
  rowId: string;
  rowIndex: number;
  transferorCd: string;
  transferorName: string;
  transferorCategoryCd: string;
  transferorId: string;
  transferorRepresentativeName: string;
  transfereeCd: string;
  transfereeName: string;
  transfereeCategoryCd: string;
  transfereeId: string;
  transfereeRepresentativeName: string;
  currencyCode: string;
  currencyName: string;
  shareClassCode: string;
  shareClassName: string;
  numberOfShares: string;
  sharesNumeral: string;
  sharesWords: string;
  considerationNumeral: string;
  considerationWords: string;
  fromCertificateNo: string;
  toCertificateNo: string;
  transferorHasWitness: boolean;
  transferorWitnessSignersJson: string;
  transfereeHasWitness: boolean;
  transfereeWitnessSignersJson: string;
}

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

function parseSharesWords(numberOfShares: string): { sharesNumeral: string; sharesWords: string } {
  const n = Number(String(numberOfShares || '').replace(/,/g, ''));
  if (!Number.isFinite(n) || n <= 0) return { sharesNumeral: '', sharesWords: '' };
  return {
    sharesNumeral: formatSharesNumeral(n),
    sharesWords: sharesToEnglishWords(n),
  };
}

function witnessJson(name: string): string {
  const n = trim(name);
  if (!n) return '';
  return JSON.stringify([{ name: n, role: 'witness' }]);
}

export function flattenTransfers(input: {
  formData: Record<string, any>;
}): FlattenedTransferRow[] {
  const formData = input.formData || {};
  const shareholders = Array.isArray(formData.shareholders) ? formData.shareholders : [];
  const rows: FlattenedTransferRow[] = [];

  shareholders.forEach((sh: any, rowIndex: number) => {
    const transferorName = trim(sh.fromShareholderName);
    const transfereeName = trim(sh.toShareholderName);
    const numberOfShares = trim(sh.fromNumberOfShares || sh.toNumberOfShares);
    const currencyName = trim(sh.currencyName) || trim(sh.currency);
    if (!transferorName || !transfereeName || !numberOfShares) return;

    const shareWords = parseSharesWords(numberOfShares);
    const consideration = syncConsiderationWordFields({
      amount: sh.totalConsiderationPaid,
      currencyName,
    });

    rows.push({
      rowId: trim(sh.id) || `row-${rowIndex}`,
      rowIndex,
      transferorCd: trim(sh.fromShareholder),
      transferorName,
      transferorCategoryCd: trim(sh.fromCategoryCd),
      transferorId: trim(sh.fromShareholderID || sh.fromContactRegNo),
      transferorRepresentativeName: trim(sh.fromRepresentativeName),
      transfereeCd: trim(sh.toShareholder),
      transfereeName,
      transfereeCategoryCd: trim(sh.toCategoryCd),
      transfereeId: trim(sh.toShareholderID || sh.toContactRegNo),
      transfereeRepresentativeName: trim(sh.toRepresentativeName),
      currencyCode: trim(sh.currency),
      currencyName,
      shareClassCode: trim(sh.shareType),
      shareClassName: trim(sh.shareTypeName),
      numberOfShares,
      sharesNumeral: shareWords.sharesNumeral,
      sharesWords: shareWords.sharesWords,
      considerationNumeral: consideration.considerationNumeral,
      considerationWords: consideration.considerationWords,
      fromCertificateNo: trim(sh.fromShareholderCertNo || sh.fromShareCertificateNo),
      toCertificateNo: trim(sh.toShareholderCertNo || sh.toShareCertificateNo),
      transferorHasWitness: !!sh.transferorHasWitness,
      transferorWitnessSignersJson: witnessJson(sh.transferorWitnessName),
      transfereeHasWitness: !!sh.transfereeHasWitness,
      transfereeWitnessSignersJson: witnessJson(sh.transfereeWitnessName),
    });
  });

  return rows;
}
